import { useState } from 'react';
import { formatHours, percentOf, SERIES, sharePercent } from '../charts/chart-theme';
import { GroupedBarChart } from '../charts/grouped-bar-chart';
import { MultiLineTrend } from '../charts/multi-line-trend';
import { StackedCategoryBar, type StackedRow } from '../charts/stacked-category-bar';
import { SegmentedToggle, type UnitMode, UnitToggle } from '../segmented-toggle';
import { WidgetCard, type WidgetInsights } from '../widget-card';
import { WidgetLegend } from '../widget-legend';
import { type Engagement, engagementSplit, FIRM, type Group, MONTHS } from './fixtures';

type Grouping = 'firm' | 'location' | 'segment';

const BILLABILITY_LEGEND = [
  { color: SERIES.billable, label: 'Billable' },
  { color: SERIES.nonBillable, label: 'Non-billable' },
];

/**
 * One engagement type (Hourly or Retainer) — its billable/non-billable split
 * for the period, and how it moved over 12 months.
 *
 * The grouping switcher answers "we need to see hourly and retainer data by
 * segment and location": at firm level it's three summary bars, and by location
 * or segment it becomes one stacked bar per group. The trend is firm-level, so
 * it only shows in the firm view — a 12-month history per group would be a
 * different (and much denser) chart.
 */
export function EngagementTile({
  engagement,
  locations,
  segments,
  insights,
  onDrillGroup,
  onDrillFirm,
}: {
  engagement: Engagement;
  locations: Group[];
  segments: Group[];
  insights: WidgetInsights;
  onDrillGroup: (group: Group) => void;
  onDrillFirm: () => void;
}) {
  const [unit, setUnit] = useState<UnitMode>('hours');
  const [grouping, setGrouping] = useState<Grouping>('firm');
  const asHours = unit === 'hours';

  const total = engagement.billable + engagement.nonBillable;
  const scale = (v: number): number => (asHours ? v : sharePercent(v, FIRM.available));
  const display = (v: number): string =>
    asHours ? formatHours(v) : `${percentOf(v, FIRM.available)}%`;

  const groups = grouping === 'segment' ? segments : locations;
  const grouped = grouping !== 'firm';

  const rows: StackedRow[] = groups.map((g) => {
    const cell = engagementSplit(g)[engagement.key];
    return {
      name: g.name,
      caption: `${g.people} ppl`,
      total: asHours ? formatHours(cell.total) : `${percentOf(cell.total, g.available)}%`,
      values: {
        billable: asHours ? cell.billable : sharePercent(cell.billable, g.available),
        nonBillable: asHours ? cell.nonBillable : sharePercent(cell.nonBillable, g.available),
      },
    };
  });

  // `Math.max()` of an empty list is -Infinity, which collapses the value axis.
  const maxValue = asHours
    ? Math.max(0, ...groups.map((g) => engagementSplit(g)[engagement.key].total))
    : 100;

  return (
    <WidgetCard
      title={engagement.name}
      subtitle={`${engagement.note} · ${percentOf(engagement.billable, total)}% billable · ${display(total)} ${asHours ? 'hrs' : 'of net-available'}`}
      controls={
        <>
          <SegmentedToggle
            label="Group by"
            value={grouping}
            onChange={setGrouping}
            options={[
              { value: 'firm', label: 'Firm' },
              { value: 'location', label: 'Location' },
              { value: 'segment', label: 'Segment' },
            ]}
          />
          <UnitToggle value={unit} onChange={setUnit} />
        </>
      }
      insights={insights}
    >
      {grouped ? (
        <StackedCategoryBar
          rows={rows}
          series={[
            { key: 'billable', label: 'Billable', color: SERIES.billable },
            { key: 'nonBillable', label: 'Non-billable', color: SERIES.nonBillable },
          ]}
          max={maxValue}
          onRowClick={(i) => {
            const group = groups[i];
            if (group) onDrillGroup(group);
          }}
        />
      ) : (
        <>
          <GroupedBarChart
            height={150}
            onBarClick={onDrillFirm}
            bars={[
              {
                label: 'Billable',
                value: scale(engagement.billable),
                color: SERIES.billable,
                display: display(engagement.billable),
              },
              {
                label: 'Non-billable',
                value: scale(engagement.nonBillable),
                color: SERIES.nonBillable,
                display: display(engagement.nonBillable),
              },
              {
                label: 'Total',
                value: scale(total),
                color: SERIES.neutral,
                display: display(total),
              },
            ]}
          />
          <div className="mt-2 border-t pt-2">
            <p className="mb-1 text-[10px] text-muted-foreground uppercase tracking-wide">
              12-month trend
            </p>
            <MultiLineTrend
              height={96}
              labels={MONTHS}
              series={[
                {
                  key: 'nonBillable',
                  label: 'Non-billable',
                  color: SERIES.nonBillable,
                  values: engagement.trend.map((v, i) => v - (engagement.trendBillable[i] ?? 0)),
                },
                {
                  key: 'billable',
                  label: 'Billable',
                  color: SERIES.billable,
                  values: engagement.trendBillable,
                },
                {
                  key: 'total',
                  label: 'Total',
                  color: SERIES.neutral,
                  values: engagement.trend,
                },
              ]}
            />
          </div>
        </>
      )}
      <WidgetLegend
        items={
          grouped
            ? BILLABILITY_LEGEND
            : [...BILLABILITY_LEGEND, { color: SERIES.neutral, label: 'Total' }]
        }
        note={grouped ? 'click a bar to drill into its people' : undefined}
      />
    </WidgetCard>
  );
}
